import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { CheckCircle2, ShieldCheck, Trophy } from 'lucide-react';
import type { PendingRegistrationPreview } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button } from '../components/ui';

/** Landing page for the "E-Mail bestätigen" link — deliberately a page with
 * an explicit button, not an auto-triggering GET the link itself performs:
 * some corporate mail gateways/link-preview features prefetch links found in
 * e-mails, which would otherwise silently burn the single-use confirm token
 * before the person ever sees it. See docs/members.md,
 * "E-Mail-Bestätigung". */
export function ConfirmRegistrationPage() {
  const { token } = useParams({ strict: false });
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const { data: preview, isLoading, isError, error } = useQuery({
    queryKey: ['pending-registration', token],
    queryFn: () => api<PendingRegistrationPreview>(`/public/registrations/${token}`),
    retry: false,
  });

  const confirm = useMutation({
    mutationFn: async () => {
      await api(`/public/registrations/${token}/confirm`, { method: 'POST' });
    },
    onSuccess: async () => {
      await refresh();
      await navigate({ to: '/' });
    },
  });

  const logoUrl = preview?.hasLogo ? `/api/public/registrations/${token}/logo` : null;

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
        <p className="text-sm text-slate-400">Einen Moment …</p>
      </div>
    );
  }

  if (isError || !preview) {
    const code = error instanceof ApiError ? error.problem.code : undefined;
    const message =
      code === 'registration_expired'
        ? 'Dieser Bestätigungslink ist abgelaufen (10 Minuten Gültigkeit). Bitte fülle das Registrierungsformular erneut aus.'
        : 'Dieser Bestätigungslink ist ungültig oder wurde bereits verwendet.';
    return (
      <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
        <div className="w-full max-w-md rounded-2xl border border-white/60 bg-white/90 p-8 text-center shadow-xl">
          <h1 className="mb-3 text-xl font-semibold text-slate-900">Link ungültig</h1>
          <Alert>{message}</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/60 bg-white/95 p-8 text-center shadow-xl">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="mx-auto mb-4 h-14 w-14 rounded-2xl border border-slate-200 bg-white object-contain p-1.5 shadow-sm"
          />
        ) : (
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-accent to-indigo-600 text-white shadow-md">
            <Trophy size={26} />
          </div>
        )}
        <h1 className="mb-1 text-xl font-semibold text-slate-900">E-Mail bestätigen</h1>
        <p className="mb-6 text-sm text-slate-500">
          Hallo {preview.firstName}, bestätige deine Registrierung bei{' '}
          <strong className="text-slate-700">{preview.organizationName}</strong>, um dein Konto anzulegen.
        </p>

        {confirm.isError ? (
          <Alert>
            {confirm.error instanceof ApiError && confirm.error.problem.code === 'email_taken'
              ? 'Für diese E-Mail-Adresse besteht inzwischen bereits ein Konto.'
              : confirm.error instanceof ApiError && confirm.error.problem.code === 'registration_expired'
                ? 'Der Link ist inzwischen abgelaufen. Bitte fülle das Registrierungsformular erneut aus.'
                : 'Das hat leider nicht funktioniert. Bitte versuche es erneut.'}
          </Alert>
        ) : (
          <Button
            type="button"
            onClick={() => confirm.mutate()}
            disabled={confirm.isPending}
            className="w-full gap-2 py-2.5"
          >
            <ShieldCheck size={16} />
            {confirm.isPending ? 'Wird bestätigt…' : 'Registrierung bestätigen'}
          </Button>
        )}

        {confirm.isSuccess && (
          <p className="mt-4 flex items-center justify-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle2 size={16} /> Bestätigt — du wirst weitergeleitet …
          </p>
        )}
      </div>
    </div>
  );
}
